import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';
import type { DrivingWeekLimits } from '../api/apiService';
import { formatLimitHours } from './drivingLimit';

// Local notifications at 80 % and 95 % of the weekly driving limit and of the
// 17-week average (ALL-136). Same model as drivingLimitNotifications (10 h in
// 24 h, unchanged): the server projects when each threshold is crossed while
// driving, and the app schedules it on every status refresh. A threshold
// already passed has no projection, so it is not scheduled again; on top of
// that, a notification whose time has passed is remembered per week, so a late
// reschedule (clock skew, app restart) cannot deliver it twice.
export const WEEK_LIMIT_WARNING_NOTIFICATION_ID = 'driving-week-limit-warning';
export const WEEK_LIMIT_CRITICAL_NOTIFICATION_ID = 'driving-week-limit-critical';
export const AVERAGE_LIMIT_WARNING_NOTIFICATION_ID = 'driving-average-limit-warning';
export const AVERAGE_LIMIT_CRITICAL_NOTIFICATION_ID = 'driving-average-limit-critical';

const ALL_IDS = [
  WEEK_LIMIT_WARNING_NOTIFICATION_ID,
  WEEK_LIMIT_CRITICAL_NOTIFICATION_ID,
  AVERAGE_LIMIT_WARNING_NOTIFICATION_ID,
  AVERAGE_LIMIT_CRITICAL_NOTIFICATION_ID,
];

export const WEEK_LIMIT_WARNING_RATIO = 0.8;
export const WEEK_LIMIT_CRITICAL_RATIO = 0.95;

type Translate = (key: string, options?: Record<string, unknown>) => string;

const cancelById = async (identifier: string) => {
  try {
    await Notifications.cancelScheduledNotificationAsync(identifier);
  } catch (error) {
    console.warn('Failed to cancel driving week limit notification', { identifier, error });
  }
};

export const cancelDrivingWeekLimitNotifications = async () => {
  await Promise.all(ALL_IDS.map(cancelById));
};

const schedule = async (identifier: string, seconds: number, title: string, body: string) => {
  try {
    await Notifications.scheduleNotificationAsync({
      identifier,
      content: { title, body, sound: true },
      trigger: {
        type: Notifications.SchedulableTriggerInputTypes.TIME_INTERVAL,
        seconds: Math.max(1, Math.round(seconds)),
        repeats: false,
      },
    });
  } catch (error) {
    console.warn('Failed to schedule driving week limit notification', { identifier, error });
  }
};

// Rounded to a tenth first: 60 h × 0.2 is 11.999… in floating point.
const hours = (seconds: number, language?: string) =>
  formatLimitHours(Math.round((seconds / 3600) * 10) * 360, language);

const isFuture = (seconds: number | null | undefined): seconds is number => seconds != null && seconds > 0;

// identifier → { week: week_start, at: epoch ms the notification was due }
type Delivered = Record<string, { week: string; at: number }>;
export const DELIVERED_STORAGE_KEY = 'drivingWeekLimitNotifications.delivered';

const readDelivered = async (): Promise<Delivered> => {
  try {
    const raw = await AsyncStorage.getItem(DELIVERED_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Delivered) : {};
  } catch {
    return {};
  }
};

const writeDelivered = async (delivered: Delivered) => {
  try {
    await AsyncStorage.setItem(DELIVERED_STORAGE_KEY, JSON.stringify(delivered));
  } catch {
    // Best effort: without it only the server projection prevents repeats.
  }
};

const applyStatus = async (
  limits: DrivingWeekLimits | null | undefined,
  isDriving: boolean,
  t: Translate,
  language?: string,
  nowMs: number = Date.now(),
) => {
  const stored = await readDelivered();
  const delivered: Delivered = {};
  for (const [id, entry] of Object.entries(stored)) {
    if (entry.at <= nowMs) delivered[id] = entry;
  }
  // Cancel only what is not due yet. Android fires these alarms late (inexact
  // window), so cancelling one whose time has passed would drop it unseen.
  await Promise.all(ALL_IDS.filter(id => !delivered[id]).map(cancelById));
  let changed = Object.keys(delivered).length !== Object.keys(stored).length;
  if (!limits || !isDriving) {
    if (changed) await writeDelivered(delivered);
    return;
  }

  const scheduleOnce = async (
    identifier: string,
    week: string,
    seconds: number | null | undefined,
    title: string,
    body: string,
  ) => {
    if (!isFuture(seconds)) return;
    const previous = delivered[identifier];
    if (previous && previous.week === week) return;
    await schedule(identifier, seconds, title, body);
    delivered[identifier] = { week, at: nowMs + Math.max(1, Math.round(seconds)) * 1000 };
    changed = true;
  };

  const { week, average } = limits;
  if (week && week.is_driving) {
    const limit = formatLimitHours(week.limit_seconds, language);
    // Values at the moment the threshold is crossed, not now.
    const at = (ratio: number) => ({
      used: hours(week.limit_seconds * ratio, language),
      limit,
      remaining: hours(week.limit_seconds * (1 - ratio), language),
    });
    const warning = at(WEEK_LIMIT_WARNING_RATIO);
    const critical = at(WEEK_LIMIT_CRITICAL_RATIO);
    await scheduleOnce(
      WEEK_LIMIT_WARNING_NOTIFICATION_ID,
      week.week_start,
      week.seconds_until_warning,
      t('workTime.notifications.weekWarningTitle', warning),
      t('workTime.notifications.weekWarningBody', warning),
    );
    await scheduleOnce(
      WEEK_LIMIT_CRITICAL_NOTIFICATION_ID,
      week.week_start,
      week.seconds_until_critical,
      t('workTime.notifications.weekWarningTitle', critical),
      t('workTime.notifications.weekWarningBody', critical),
    );
  }
  if (average && average.is_driving) {
    const limit = formatLimitHours(average.limit_weekly_seconds, language);
    const at = (ratio: number) => ({ avg: hours(average.limit_weekly_seconds * ratio, language), limit });
    // The average window moves by one week every Monday, like the week total.
    const window = average.window_end;
    const warning = at(WEEK_LIMIT_WARNING_RATIO);
    const critical = at(WEEK_LIMIT_CRITICAL_RATIO);
    await scheduleOnce(
      AVERAGE_LIMIT_WARNING_NOTIFICATION_ID,
      window,
      average.seconds_until_warning,
      t('workTime.notifications.fourMonthWarningTitle', warning),
      t('workTime.notifications.fourMonthWarningBody', warning),
    );
    await scheduleOnce(
      AVERAGE_LIMIT_CRITICAL_NOTIFICATION_ID,
      window,
      average.seconds_until_critical,
      t('workTime.notifications.fourMonthWarningTitle', critical),
      t('workTime.notifications.fourMonthWarningBody', critical),
    );
  }
  if (changed) await writeDelivered(delivered);
};

// Syncs run one after another, as in drivingLimitNotifications.
let syncQueue: Promise<void> = Promise.resolve();

/**
 * Bring the scheduled week and average notifications in line with the latest
 * server status. Passing no status (or not driving) only cancels.
 */
export const syncDrivingWeekLimitNotifications = (
  limits: DrivingWeekLimits | null | undefined,
  isDriving: boolean,
  t: Translate,
  language?: string,
  nowMs?: number,
): Promise<void> => {
  if (Platform.OS === 'web') return Promise.resolve();
  syncQueue = syncQueue.then(() => applyStatus(limits, isDriving, t, language, nowMs)).catch(() => undefined);
  return syncQueue;
};
